import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Text, Button, Card } from '@tremor/react';
import { SparklesIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { generateTrArtifact, getTrArtifact } from '../../Requests/TendencyReportRequests';
import { useArtifact } from './useArtifact';
import UpgradedLoader from '../UpgradedLoader';

const FIELDS = [
    ['basic_needs', 'BasicNeeds'],
    ['motivating_factor', 'MotivatingFactor'],
    ['consumption_expectations', 'ConsumptionExpectations'],
    ['inspiration', 'Inspiration'],
    ['implementation_suggestions', 'Implementation'],
];

/** The AI may return a field as a list or an object; render it as readable text. */
const toText = (v) => {
    if (Array.isArray(v)) return v.map(toText).filter(Boolean).join('; ');
    if (v && typeof v === 'object') return Object.values(v).map(toText).filter(Boolean).join('; ');
    return v == null ? '' : String(v);
};

const TYPE_CLASS = {
    trend: 'bg-green-100 text-green-800',
    fashion: 'bg-amber-100 text-amber-800',
    wave: 'bg-purple-100 text-purple-800',
};

/** Step 3: trend insights crossing sources + report + collected fieldwork. */
function TrInsightsTab({ projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const get = useCallback(() => getTrArtifact(projectUuid, analysisUuid, 'tr_insights'), [projectUuid, analysisUuid]);
    const gen = useCallback(() => generateTrArtifact(projectUuid, analysisUuid, 'tr_insights'), [projectUuid, analysisUuid]);
    const { status, result, loading, generate } = useArtifact({ get, generate: gen });
    const insights = Array.isArray(result) ? result : [];

    return (
        <div data-testid="tr-insights-tab">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                    <LightBulbIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">{t('TendencyReport.Insights.Title')}</Title>
                </div>
                {status !== 'processing' && (
                    <Button icon={SparklesIcon} onClick={generate} className="bg-iavisus-900">
                        {status === 'completed' ? t('TendencyReport.Regenerate') : t('TendencyReport.Generate')}
                    </Button>
                )}
            </div>
            <Text className="mb-4">{t('TendencyReport.Insights.Description')}</Text>

            {(loading || status === 'processing') && <UpgradedLoader />}
            {status === 'failed' && <Text className="text-red-600">{t('TendencyReport.Error')}</Text>}
            {status === 'not_started' && !loading && <Text className="text-gray-500">{t('TendencyReport.Insights.CollectDataFirst')}</Text>}

            {status === 'completed' && insights.length > 0 && (
                <div className="space-y-4">
                    {insights.map((ins, i) => (
                        <Card key={i} className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Title className="text-base font-bold text-iavisus-100">{toText(ins.title)}</Title>
                                {ins.type && (
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_CLASS[ins.type] || 'bg-gray-100 text-gray-700'}`}>
                                        {t(`TendencyReport.Insights.Type.${ins.type}`)}
                                    </span>
                                )}
                            </div>
                            <div className="space-y-2">
                                {FIELDS.map(([key, labelKey]) => (
                                    <div key={key}>
                                        <Text className="font-bold text-xs uppercase text-gray-500">{t(`TendencyReport.Insights.${labelKey}`)}</Text>
                                        <Text>{toText(ins[key])}</Text>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

export default TrInsightsTab;
